import { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  Group,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import * as _ from 'lodash';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';

import classes from './Links.module.css';

interface LinksGroupProps {
  icon?: any;
  label: string;
  initiallyOpened?: boolean;
  link?: string;
  links?: {
    label: string;
    link: string;
  }[];
  closeSidebar: () => void;
  isMini?: boolean;
}

export function LinksGroup(props: LinksGroupProps) {
  const {
    icon: Icon,
    label,
    initiallyOpened,
    link,
    links,
    closeSidebar,
    isMini,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const hasLinks = Array.isArray(links);
  const [opened, setOpened] = useState(initiallyOpened || false);
  const [currentPath, setCurrentPath] = useState<string | undefined>();
  const ChevronIcon = IconChevronRight;

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    if (hasLinks) {
      setOpened((o) => !o);
    } else if (link) {
      navigate(link);
      if (isMobile) {
        closeSidebar();
      }
    }
  };

  const LinkItem = ({ link }: { link: { label: string; link: string } }) => {
    const content = (
      <NavLink
        to={link.link}
        className={({ isActive }) =>
          `${classes.link} ${isActive ? classes.linkActive : ''}`
        }
      >
        <Text
          component="button"
          className={classes.link}
          data-active={currentPath === link.link || undefined}
          data-mini={isMini || undefined}
        >
          {link.label}
        </Text>
      </NavLink>
    );

    return isMini ? (
      <Tooltip label={link.label} position="right" withArrow>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  const items = (hasLinks ? links : []).map((link) => (
    <LinkItem key={link.label} link={link} />
  ));

  const buttonContent = (
    <UnstyledButton
      onClick={handleClick}
      className={classes.control}
      data-active={(!hasLinks ? currentPath === link : hasLinks && opened) || undefined}
      data-mini={isMini || undefined}
    >
      <Group justify="space-between" gap={0}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Box mr={isMini ? 0 : 'md'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Icon && <Icon size="1.1rem" stroke={1.5} />}
          </Box>
          {!isMini && <Box>{label}</Box>}
        </Box>
        {hasLinks && !isMini && (
          <ChevronIcon
            className={classes.chevron}
            size="1rem"
            stroke={1.5}
            style={{
              transform: opened ? 'rotate(90deg)' : 'none',
            }}
          />
        )}
      </Group>
    </UnstyledButton>
  );

  return (
    <>
      {isMini ? (
        <Tooltip label={label} position="right" withArrow>
          {buttonContent}
        </Tooltip>
      ) : (
        buttonContent
      )}
      {hasLinks && !isMini && (
        <Collapse in={opened} className={classes.links}>
          {items}
        </Collapse>
      )}
      {hasLinks && isMini && opened && (
        <Menu opened={opened} onClose={() => setOpened(false)} position="right-start">
          <Menu.Dropdown>{items}</Menu.Dropdown>
        </Menu>
      )}
    </>
  );
}